import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCMSStore } from "@/hooks/use-cms-store";

const cmsIntegrations = z.object({
  integrations_google_analytics_measurement_id: z.string(),
  integrations_linkedin_insight_partner_id: z.string(),
  integrations_meta_pixel_tracking_id: z.string(),
});

export const CMSIntegrations: FC = () => {
  const pageContent = useCMSStore.use.pageContent();
  const form = useForm<z.infer<typeof cmsIntegrations>>({
    resolver: zodResolver(cmsIntegrations),
    defaultValues: {
      integrations_google_analytics_measurement_id: get(
        pageContent,
        "options.integrations.google_analytics.measurement_id",
        "",
      ),
      integrations_linkedin_insight_partner_id: get(
        pageContent,
        "options.integrations.linkedin_insight.partner_id",
        "",
      ),
      integrations_meta_pixel_tracking_id: get(
        pageContent,
        "options.integrations.meta_pixel.tracking_id",
        "",
      ),
    },
  });

  const onUpdate = useCMSStore.use.onUpdateIntegrations();

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate({
          google_analytics: {
            measurement_id: values.integrations_google_analytics_measurement_id,
          },
          linkedin_insight: {
            partner_id: values.integrations_linkedin_insight_partner_id,
          },
          meta_pixel: {
            tracking_id: values.integrations_meta_pixel_tracking_id,
          },
        });
      },
    });

    return unsubscribe;
  }, [form, onUpdate]);

  return (
    <div className="flex flex-col gap-6">
      <Form {...form}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Meta Pixel</CardTitle>
            <CardDescription className="font-medium">
              Start tracking events on your site by adding your Meta Pixel ID.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="integrations_meta_pixel_tracking_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta Pixel ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Example: 012345678901234" {...field} />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    <a
                      href="https://www.facebook.com/business/tools/meta-pixel"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Create a Meta Pixel
                    </a>{" "}
                    and paste it here.
                  </FormDescription>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Google Analytics</CardTitle>
            <CardDescription className="font-medium">
              Start tracking events on your site by adding your Google Analytics
              Measurement ID.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="integrations_google_analytics_measurement_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Measurement ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Example: G-0123456789" {...field} />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Create a new property in your{" "}
                    <a
                      href="https://support.google.com/analytics/answer/9304153"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google Analytics account
                    </a>
                    .{" "}
                    <a
                      href="https://support.google.com/analytics/answer/9539598"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Find your measurement ID here
                    </a>
                    .
                  </FormDescription>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">LinkedIn Insight</CardTitle>
            <CardDescription className="font-medium">
              Get in-depth campaign reporting and website retargeting with
              LinkedIn Insight Tag.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="integrations_linkedin_insight_partner_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LinkedIn Partner ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Example: 01234" {...field} />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Paste your Linkedin partner ID, and go to the tag manager
                    you want to use.{" "}
                    <a
                      href="https://www.linkedin.com/help/lms/answer/a422760"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more about using tag managers
                    </a>
                    .
                  </FormDescription>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      </Form>
    </div>
  );
};
